import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from 'components/Page';
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DescriptionListItem from "components/DescriptionListItem";
import {Greeting, GreetingData} from "types/Greeting";
import Button from "components/Button";
import {CheckCircleIcon, PencilIcon, TrashIcon, XCircleIcon} from "@heroicons/react/solid";
import DeleteGreeting from '../Delete';
import {formattedDateTime} from "services/date";
import BooleanIndicator from "components/BooleanIndicator";
import {invalidate} from "../../../services/query-client";
import {useNotifications} from "../../../context/NotificationContext";
import Gallery from "../../../components/Gallery";

function ViewGreeting() {
    const {id} = useParams()
    const {data: greeting, isLoading} = useQuery(
        ['greetings', {id}],
        () => ngage.greetings.get(parseInt(id as string))
    )
    
    const update = useMutation((data: GreetingData) => ngage.greetings.update(Number(id), data))
    
    const [deletingGreeting, setDeletingGreeting] = useState<Greeting | null>(null);

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prepare action handlers
    function handleDeleteGreeting() {
        if(greeting) setDeletingGreeting(greeting)
    }

    function handleEditGreeting() {
        if(id) navigate(`/greetings/${id}/edit`)
    }

    async function handleEnableGreeting() {
        if(!greeting) {
            return
        }

        const enabling = !greeting.is_enabled

        try{
            await update.mutateAsync({is_enabled: enabling})
            await invalidate(['greetings', {id}])

            if(enabling){
                notify("success", "Greeting Enabled", `${greeting.name} has been enabled and will now be displayed`)
            } else {
                notify("success", "Greeting Disabled", `${greeting.name} has been disabled and will not be displayed`)
            }
        } catch (error) {
            notify("error", "Greeting Not Updated", `${greeting.name} enablement could not be changed`)
        }
    }

    // Prepare breadcrumbs
    const breadcrumbs = {
        "Content": "/",
        "Greetings": "/greetings",
        "Greeting": `/greetings/${id}`
    }

    // Prepare actions
    const actions = <>
        <Button
            color={Button.colors.WHITE}
            icon={TrashIcon}
            onClick={handleDeleteGreeting}
            disabled={!greeting}
        >
            Delete
        </Button>

        <Button
            color={Button.colors.PRIMARY}
            icon={PencilIcon}
            onClick={handleEditGreeting}
            disabled={!greeting}
        >
            Edit
        </Button>

        <Button
            color={Button.colors.PRIMARY}
            icon={greeting?.is_enabled? XCircleIcon : CheckCircleIcon}
            onClick={handleEnableGreeting}
            showSpinner={update.isLoading}
        >
            {greeting?.is_enabled? 'Disable' : 'Enable'}
        </Button>
    </>

    return (
        <Page title={greeting?.title || "View Greeting"} breadcrumbs={breadcrumbs} actions={actions} isLoading={isLoading}>
            {greeting &&
                <>
                    {/* Greeting Information */}
                    <section className="flex flex-col lg:flex-row-reverse lg:justify-end">
                        {/*{greeting.image &&*/}
                        {/*    <div className="flex justify-center items-center max-h-36 lg:max-h-full shadow overflow-hidden sm:rounded-md sm:mb-2 lg:mb-0 lg:ml-4">*/}
                        {/*        <img*/}
                        {/*            src={greeting.image.preview_url}*/}
                        {/*            alt={greeting.title}*/}
                        {/*            className="object-cover w-full lg:h-full lg:w-auto"*/}
                        {/*        />*/}
                        {/*    </div>*/}
                        {/*}*/}

                        <div className="lg:basis-2/3 lg:flex-shrink-0 lg:flex-grow">
                            <Card
                                heading="Greeting Details"
                                subheading={`Created ${formattedDateTime(greeting.created_at)}`}
                            >
                                <DescriptionList>
                                    <DescriptionListItem span={1}>
                                        <DescriptionTerm>Enabled</DescriptionTerm>
                                        <DescriptionDetails>
                                            <BooleanIndicator value={greeting.is_enabled} />
                                        </DescriptionDetails>
                                    </DescriptionListItem>

                                    <DescriptionListItem span={1}>
                                        <DescriptionTerm>Enabled Since</DescriptionTerm>
                                        <DescriptionDetails>
                                            {greeting.is_enabled? formattedDateTime(greeting.enabled_at) : 'N/A'}
                                        </DescriptionDetails>
                                    </DescriptionListItem>

                                </DescriptionList>
                            </Card>
                        </div>
                    </section>

                    {/* Content */}
                    <section className="mt-4">
                        <Card heading={greeting.title ?? ''} >
                            <p className="whitespace-pre-line">
                                {greeting.body}
                            </p>
                        </Card>
                    </section>

                    {/* Media */}
                    {!!greeting.media.length &&
                        <section className="mt-4">
                            <Card heading="Media">
                                <Gallery media={greeting.media} />
                            </Card>
                        </section>
                    }
                </>
            }

            {/* Delete confirmation modal */}
            <DeleteGreeting
                show={greeting === deletingGreeting}
                greeting={deletingGreeting}
                onCancel={() => setDeletingGreeting(null)}
                onDelete={() => navigate('/greetings')}
                onDeleteFail={() => setDeletingGreeting(null)}
            />
        </Page>
    );
}

export default ViewGreeting;